"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EntityManager = void 0;
var tslib_1 = require("tslib");
var messages = tslib_1.__importStar(require("./message"));
var binding_1 = require("./binding");
var util_1 = require("./util");
var connector_1 = require("./connector");
var caching_1 = require("./caching");
var GeoPoint_1 = require("./GeoPoint");
var metamodel_1 = require("./metamodel");
var query_1 = require("./query");
var error_1 = require("./error");
var intersection_1 = require("./intersection");
var Message_1 = require("./connector/Message");
var MFAError_1 = require("./error/MFAError");
var message = tslib_1.__importStar(require("./message"));
var DB_PREFIX = '/db/';
var EntityManager = /** @class */ (function (_super) {
    tslib_1.__extends(EntityManager, _super);
    /**
     * @param entityManagerFactory The factory which of this entityManager instance
     */
    function EntityManager(entityManagerFactory) {
        var _this = _super.call(this) || this;
        /**
         * Constructor for a new List collection
         */
        _this.List = Array;
        /**
         * Constructor for a new Set collection
         */
        _this.Set = Set;
        /**
         * Constructor for a new Map collection
         */
        _this.Map = Map;
        /**
         * Constructor for a new GeoPoint
         */
        _this.GeoPoint = GeoPoint_1.GeoPoint;
        /**
         * Log messages can created by calling log directly as function, with a specific log level or with the helper
         * methods, which a members of the log method.
         *
         * Logs will be filtered by the client logger and the before they persisted. The default log level is
         * 'info' therefore all log messages below the given message aren't persisted.
         *
         * Examples:
         * <pre class="prettyprint">
         // default log level ist info
         db.log('test message %s', 'my string');
         // info: test message my string
      
         // pass a explicit log level as the first argument, one of ('trace', 'debug', 'info', 'warn', 'error')
         db.log('warn', 'test message %d', 123);
         // warn: test message 123
      
         // debug log level will not be persisted by default, since the default logging level is info
         db.log('debug', 'test message %j', {number: 123}, {});
         // debug: test message {"number":123}
         // data = {}
      
         // One additional json object can be provided, which will be persisted together with the log entry
         db.log('info', 'test message %s, %s', 'first', 'second', {number: 123});
         // info: test message first, second
         // data = {number: 123}
      
         //use the log level helper
         db.log.info('test message', 'first', 'second', {number: 123});
         // info: test message first second
         // data = {number: 123}
      
         //change the default log level to trace, i.e. all log levels will be persisted, note that the log level can be
         //additionally configured in the baqend
         db.log.level = 'trace';
      
         //trace will be persisted now
         db.log.trace('test message', 'first', 'second', {number: 123});
         // info: test message first second
         // data = {number: 123}
         * </pre>
         */
        _this.log = intersection_1.Logger.create(_this);
        /**
         * The connector used for requests
         */
        _this.connection = null;
        /**
         * All managed and cached entity instances
         * @type Map<String,Entity>
         * @private
         */
        _this.entities = {};
        _this.modules = new intersection_1.Modules(_this);
        /**
         * The current logged in user object
         */
        _this.me = null;
        /**
         * The current registered device object
         */
        _this.deviceMe = null;
        /**
         * Returns the tokenStorage which will be used to authorize all requests.
         */
        _this.tokenStorage = null; // is never null after em is ready
        /**
         * The bloom filter which contains staleness information of cached objects
         */
        _this.bloomFilter = null;
        /**
         * Set of object ids that were revalidated after the Bloom filter was loaded.
         */
        _this.cacheWhiteList = null;
        /**
         * Set of object ids that were updated but are not yet included in the bloom filter.
         * This set essentially implements revalidation by side effect which does not work in Chrome.
         */
        _this.cacheBlackList = null;
        /**
         * Bloom filter refresh interval in seconds.
         */
        _this.bloomFilterRefresh = 60;
        /**
         * Bloom filter refresh Promise
         */
        _this.bloomFilterLock = new util_1.Lockable();
        /**
         * A File factory for file objects.
         * The file factory can be called to create new instances for files.
         * The created instances implements the {@link File} interface
         */
        _this.File = null; // is never null after the em is ready
        /**
         * the shared connection data if this EntityManager shares the credentials with the EntityManagerFactory
         * @private
         */
        _this.connectData = null;
        _this.entityManagerFactory = entityManagerFactory;
        _this.metamodel = entityManagerFactory.metamodel;
        _this.code = entityManagerFactory.code;
        return _this;
    }
    Object.defineProperty(EntityManager.prototype, "isOpen", {
        /**
         * Determine whether the entity manager is open.
         * true until the entity manager has been closed
         */
        get: function () {
            return !!this.connection;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EntityManager.prototype, "token", {
        /**
         * The authentication token if the user is logged in currently
         */
        get: function () {
            return this.tokenStorage.token;
        },
        /**
         * The authentication token if the user is logged in currently
         * @param value
         */
        set: function (value) {
            this.tokenStorage.update(value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EntityManager.prototype, "isCachingDisabled", {
        /**
         * Whether caching is disabled
         * @deprecated
         */
        get: function () {
            return !this.isCachingEnabled();
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Whether caching is enabled
     */
    EntityManager.prototype.isCachingEnabled = function () {
        return !!this.bloomFilter;
    };
    Object.defineProperty(EntityManager.prototype, "isDeviceRegistered", {
        /**
         * Returns true if the device token is already registered, otherwise false.
         */
        get: function () {
            return !!this.deviceMe;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Connects this entityManager, used for synchronous and asynchronous initialization
     * @param useSharedTokenStorage Indicates if the shared credentials should be used
     */
    EntityManager.prototype.connected = function (useSharedTokenStorage) {
        var _a;
        this.connection = this.entityManagerFactory.connection;
        this.bloomFilterRefresh = this.entityManagerFactory.staleness;
        this.tokenStorage = useSharedTokenStorage
            ? this.entityManagerFactory.tokenStorage : new intersection_1.TokenStorage(this.connection.origin);
        this.connectData = useSharedTokenStorage ? this.entityManagerFactory.connectData : null;
        this.File = binding_1.FileFactory.create(this);
        this._createObjectFactory(this.metamodel.embeddables);
        this._createObjectFactory(this.metamodel.entities);
        if (this.connectData) {
            if (this.connectData.device) {
                this._updateDevice(this.connectData.device);
            }
            if (this.connectData.user) {
                this._updateUser(this.connectData.user, true);
            }
        }
        var bloomFilter = (_a = this.entityManagerFactory.connectData) === null || _a === void 0 ? void 0 : _a.bloomFilter;
        if (this.bloomFilterRefresh > 0 && bloomFilter && typeof util_1.atob !== 'undefined' && !util_1.isNode) {
            this._updateBloomFilter(bloomFilter);
        }
    };
    /**
     * @param types
     * @return    * @private
     */
    EntityManager.prototype._createObjectFactory = function (types) {
        var values = Object.values(types);
        var _loop_1 = function (i) {
            var type = values[i];
            var name_1 = type.name;
            if (this_1[name_1]) {
                type.typeConstructor = this_1[name_1];
                Object.defineProperty(this_1, name_1, {
                    value: type.createObjectFactory(this_1),
                });
            }
            else {
                Object.defineProperty(this_1, name_1, {
                    get: function () {
                        Object.defineProperty(this, name_1, {
                            value: type.createObjectFactory(this),
                        });
                        return this[name_1];
                    },
                    set: function (typeConstructor) {
                        type.typeConstructor = typeConstructor;
                    },
                    configurable: true,
                });
            }
        };
        var this_1 = this;
        for (var i = 0; i < values.length; i += 1) {
            _loop_1(i);
        }
    };
    EntityManager.prototype.send = function (message, ignoreCredentialError) {
        var _this = this;
        if (ignoreCredentialError === void 0) { ignoreCredentialError = true; }
        if (!this.connection) {
            throw new Error('This EntityManager is not connected.');
        }
        var msg = message;
        msg.tokenStorage(this.tokenStorage);
        var result = this.connection.send(msg);
        if (!ignoreCredentialError) {
            result = result.catch(function (e) {
                if (e.status === connector_1.StatusCode.BAD_CREDENTIALS) {
                    _this._logout();
                }
                throw e;
            });
        }
        return result;
    };
    /**
     * Get an instance whose state may be lazily fetched
     *
     * If the requested instance does not exist in the database, the
     * EntityNotFoundError is thrown when the instance state is first accessed.
     * The application should not expect that the instance state will be available upon detachment,
     * unless it was accessed by the application while the entity manager was open.
     *
     * @param entityClass
     * @param key
     * @return
     */
    EntityManager.prototype.getReference = function (entityClass, key) {
        var id = null;
        var type;
        if (key) {
            var keyAsStr = key;
            type = this.metamodel.entity(entityClass);
            if (keyAsStr.indexOf(DB_PREFIX) === 0) {
                id = keyAsStr;
            }
            else {
                id = "".concat(type.ref, "/").concat(encodeURIComponent(keyAsStr));
            }
        }
        else if (typeof entityClass === 'string') {
            var keyIndex = entityClass.indexOf('/', DB_PREFIX.length); // skip /db/
            if (keyIndex !== -1) {
                id = entityClass;
            }
            type = this.metamodel.entity(keyIndex !== -1 ? entityClass.substring(0, keyIndex) : entityClass);
        }
        else {
            type = this.metamodel.entity(entityClass);
        }
        var entity = this.entities[id];
        if (!entity) {
            entity = type.create();
            var metadata = intersection_1.Metadata.get(entity);
            if (id) {
                metadata.id = id;
            }
            metadata.setUnavailable();
            this._attach(entity);
        }
        return entity;
    };
    /**
     * Creates an instance of {@link Builder<T>} for query creation and execution
     *
     * The query results are instances of the resultClass argument.
     *
     * @param resultClass - the type of the query result
     * @return A query builder to create one ore more queries for the specified class
     */
    EntityManager.prototype.createQueryBuilder = function (resultClass) {
        return new query_1.Builder(this, resultClass);
    };
    /**
     * Clear the persistence context, causing all managed entities to become detached
     *
     * Changes made to entities that have not been flushed to the database will not be persisted.
     *
     * @return
     */
    EntityManager.prototype.clear = function () {
        this.entities = {};
    };
    /**
     * Close an application-managed entity manager
     *
     * After the close method has been invoked, all methods on the EntityManager instance
     * and any Query and TypedQuery objects obtained from it will throw the IllegalStateError
     * and isOpen (which will return false).
     *
     * @return
     */
    EntityManager.prototype.close = function () {
        this.connection = null;
        return this.clear();
    };
    /**
     * Check if the instance is a managed entity instance belonging to the current persistence context
     *
     * @param entity - entity instance
     * @return boolean indicating if entity is in persistence context
     */
    EntityManager.prototype.contains = function (entity) {
        return !!entity && !!entity.id && this.entities[entity.id] === entity;
    };
    /**
     * Check if an object with the id from the given entity is already attached
     *
     * @param entity - entity instance
     * @return boolean indicating if entity with same id is attached
     */
    EntityManager.prototype.containsById = function (entity) {
        return !!(entity && entity.id && this.entities[entity.id]);
    };
    /**
     * Remove the given entity from the persistence context, causing a managed entity to become detached
     *
     * Unflushed changes made to the entity if any (including removal of the entity),
     * will not be synchronized to the database. Entities which previously referenced the detached entity will continue
     * to reference it.
     *
     * @param entity The entity instance to detach.
     * @return
     */
    EntityManager.prototype.detach = function (entity) {
        var _this = this;
        var state = intersection_1.Metadata.get(entity);
        return state.withLock(function () {
            _this.removeReference(entity);
            return Promise.resolve(entity);
        });
    };
    /**
     * Resolve the depth by loading the referenced objects of the given entity
     *
     * @param entity - entity instance
     * @param [options] The load options
     * @return
     */
    EntityManager.prototype.resolveDepth = function (entity, options) {
        var _this = this;
        if (!options || !options.depth) {
            return Promise.resolve(entity);
        }
        var resolved = options.resolved || [];
        var promises = [];
        var subOptions = tslib_1.__assign(tslib_1.__assign({}, options), { resolved: resolved, depth: options.depth === true ? true : options.depth - 1 });
        this.getSubEntities(entity, 1).forEach(function (subEntity) {
            if (subEntity !== null && resolved.indexOf(subEntity) === -1 && subEntity.id) {
                resolved.push(subEntity);
                promises.push(_this.load(subEntity.id, undefined, subOptions));
            }
        });
        return Promise.all(promises).then(function () { return entity; });
    };
    /**
     * Search for an entity of the specified oid
     *
     * If the entity instance is contained in the persistence context, it is returned from there.
     *
     * @param entityClass - entity class
     * @param oid - Object ID
     * @param [options] The load options.
     * @return the loaded entity or null
     */
    EntityManager.prototype.load = function (entityClass, oid, options) {
        var _this = this;
        var opt = options || {};
        var entity = this.getReference(entityClass, oid);
        var state = intersection_1.Metadata.get(entity);
        if (!opt.refresh && opt.local && state.isAvailable) {
            return this.resolveDepth(entity, opt);
        }
        var msg = new messages.GetObject(state.bucket, state.key);
        this.ensureCacheHeader(entity.id, msg, opt.refresh);
        return this.send(msg).then(function (response) {
            // refresh object if loaded older version from cache
            // chrome doesn't using cache when ifNoneMatch is set
            if (entity.version && entity.version > response.entity.version) {
                opt.refresh = true;
                return _this.load(entityClass, oid, opt);
            }
            _this.addToWhiteList(response.entity.id);
            if (response.status !== connector_1.StatusCode.NOT_MODIFIED) {
                state.type.fromJsonValue(state, response.entity, entity, { persisting: true });
            }
            return _this.resolveDepth(entity, opt);
        }, function (e) {
            if (e.status === connector_1.StatusCode.OBJECT_NOT_FOUND) {
                _this.removeReference(entity);
                state.setRemoved();
                return null;
            }
            throw e;
        });
    };
    /**
     * @param entity
     * @param options
     * @return
     */
    EntityManager.prototype.insert = function (entity, options) {
        var _this = this;
        var opt = options || {};
        var isNew;
        return this._save(entity, opt, function (state, json) {
            if (state.version) {
                throw new error_1.PersistentError('Existing objects can\'t be inserted.');
            }
            isNew = !state.id;
            return new messages.CreateObject(state.bucket, json);
        }).then(function (val) {
            if (isNew) {
                _this._attach(entity);
            }
            return val;
        });
    };
    /**
     * @param entity
     * @param options
     * @return
     */
    EntityManager.prototype.update = function (entity, options) {
        var opt = options || {};
        return this._save(entity, opt, function (state, json) {
            if (!state.version) {
                throw new error_1.PersistentError('New objects can\'t be inserted.');
            }
            if (opt.force) {
                var version = json.version, jsonWithoutVersion = tslib_1.__rest(json, ["version"]);
                return new messages.ReplaceObject(state.bucket, state.key, jsonWithoutVersion)
                    .ifMatch('*');
            }
            return new messages.ReplaceObject(state.bucket, state.key, json)
                .ifMatch("".concat(state.version));
        });
    };
    /**
     * @param entity
     * @param options The save options
     * @param withoutLock Set true to save the entity without locking
     * @return
     */
    EntityManager.prototype.save = function (entity, options, withoutLock) {
        if (withoutLock === void 0) { withoutLock = false; }
        var opt = options || {};
        var msgFactory = function (state, json) {
            if (opt.force) {
                if (!state.id) {
                    throw new error_1.PersistentError('New special objects can\'t be forcefully saved.');
                }
                var version = json.version, jsonWithoutVersion = tslib_1.__rest(json, ["version"]);
                return new messages.ReplaceObject(state.bucket, state.key, jsonWithoutVersion);
            }
            if (state.version) {
                return new messages.ReplaceObject(state.bucket, state.key, json)
                    .ifMatch(state.version);
            }
            return new messages.CreateObject(state.bucket, json);
        };
        return withoutLock ? this._locklessSave(entity, opt, msgFactory) : this._save(entity, opt, msgFactory);
    };
    /**
     * @param entity
     * @param cb pre-safe callback
     * @return
     */
    EntityManager.prototype.optimisticSave = function (entity, cb) {
        var _this = this;
        return intersection_1.Metadata.get(entity).withLock(function () { return _this._optimisticSave(entity, cb); });
    };
    /**
     * @param entity
     * @param cb pre-safe callback
     * @return
     * @private
     */
    EntityManager.prototype._optimisticSave = function (entity, cb) {
        var _this = this;
        var abort = false;
        var abortFn = function () {
            abort = true;
        };
        var promise = Promise.resolve(cb(entity, abortFn));
        if (abort) {
            return Promise.resolve(entity);
        }
        return promise.then(function () { return (_this.save(entity, {}, true)
            .catch(function (e) {
            if (e.status === 412) {
                return _this.refresh(entity, {})
                    .then(function () { return _this._optimisticSave(entity, cb); });
            }
            throw e;
        })); });
    };
    /**
     * Save the object state without locking
     * @param entity
     * @param options
     * @param msgFactory
     * @return
     * @private
     */
    EntityManager.prototype._locklessSave = function (entity, options, msgFactory) {
        var _this = this;
        this.attach(entity);
        var state = intersection_1.Metadata.get(entity);
        var refPromises;
        var json;
        if (state.isAvailable) {
            // getting json will check all collections changes, therefore we must do it before proofing the dirty state
            json = state.type.toJsonValue(state, entity, {
                persisting: true,
            });
        }
        if (state.isDirty) {
            if (!options.refresh) {
                state.setPersistent();
            }
            var sendPromise = this.send(msgFactory(state, json)).then(function (response) {
                if (state.id && state.id !== response.entity.id) {
                    _this.removeReference(entity);
                    state.id = response.entity.id;
                    _this._attach(entity);
                }
                state.type.fromJsonValue(state, response.entity, entity, {
                    persisting: options.refresh,
                    onlyMetadata: !options.refresh,
                });
                return entity;
            }, function (e) {
                if (e.status === connector_1.StatusCode.OBJECT_NOT_FOUND) {
                    _this.removeReference(entity);
                    state.setRemoved();
                    return null;
                }
                state.setDirty();
                throw e;
            });
            refPromises = [sendPromise];
        }
        else {
            refPromises = [Promise.resolve(entity)];
        }
        var subOptions = tslib_1.__assign({}, options);
        subOptions.depth = 0;
        this.getSubEntities(entity, options.depth).forEach(function (sub) {
            refPromises.push(_this._save(sub, subOptions, msgFactory));
        });
        return Promise.all(refPromises).then(function () { return entity; });
    };
    /**
     * Save and lock the object state
     * @param entity
     * @param options
     * @param msgFactory
     * @return
     * @private
     */
    EntityManager.prototype._save = function (entity, options, msgFactory) {
        var _this = this;
        this.ensureBloomFilterFreshness();
        var state = intersection_1.Metadata.get(entity);
        if (state.version) {
            this.addToBlackList(entity.id);
        }
        return state.withLock(function () { return _this._locklessSave(entity, options, msgFactory); });
    };
    /**
     * Returns all referenced sub entities for the given depth and root entity
     * @param entity
     * @param depth
     * @param [resolved]
     * @param initialEntity
     * @return
     */
    EntityManager.prototype.getSubEntities = function (entity, depth, resolved, initialEntity) {
        if (resolved === void 0) { resolved = []; }
        if (!depth) {
            return resolved;
        }
        var obj = initialEntity || entity;
        var state = intersection_1.Metadata.get(entity);
        var iter = state.type.references();
        for (var item = iter.next(); !item.done; item = iter.next()) {
            var value = item.value;
            var subEntities = this.getSubEntitiesByPath(entity, value.path);
            for (var i = 0, len = subEntities.length; i < len; i += 1) {
                var subEntity = subEntities[i];
                if (resolved.indexOf(subEntity) === -1 && subEntity !== obj) {
                    resolved.push(subEntity);
                    this.getSubEntities(subEntity, depth === true ? depth : depth - 1, resolved, obj);
                }
            }
        }
        return resolved;
    };
    /**
     * Returns all referenced one level sub entities for the given path
     * @param entity
     * @param path
     * @return
     */
    EntityManager.prototype.getSubEntitiesByPath = function (entity, path) {
        var _this = this;
        var subEntities = [entity];
        path.forEach(function (attributeName) {
            var tmpSubEntities = [];
            subEntities.forEach(function (subEntity) {
                var curEntity = subEntity[attributeName];
                if (!curEntity) {
                    return;
                }
                var attribute = _this.metamodel.managedType(subEntity.constructor).getAttribute(attributeName);
                if (attribute instanceof metamodel_1.PluralAttribute) {
                    var iter = curEntity.entries();
                    for (var item = iter.next(); !item.done; item = iter.next()) {
                        var entry = item.value;
                        tmpSubEntities.push(entry[1]);
                        if (attribute instanceof metamodel_1.MapAttribute && attribute.keyType.isEntity) {
                            tmpSubEntities.push(entry[0]);
                        }
                    }
                }
                else {
                    tmpSubEntities.push(curEntity);
                }
            });
            subEntities = tmpSubEntities;
        });
        return subEntities;
    };
    /**
     * Delete the entity instance.
     * @param entity
     * @param options The delete options
     * @return
     */
    EntityManager.prototype.delete = function (entity, options) {
        var _this = this;
        var opt = options || {};
        this.attach(entity);
        var state = intersection_1.Metadata.get(entity);
        return state.withLock(function () {
            if (!state.version && !opt.force) {
                throw new error_1.IllegalEntityError(entity);
            }
            var msg = new messages.DeleteObject(state.bucket, state.key);
            _this.addToBlackList(entity.id);
            if (!opt.force) {
                msg.ifMatch("".concat(state.version));
            }
            var refPromises = [_this.send(msg).then(function () {
                    _this.removeReference(entity);
                    state.setRemoved();
                    return entity;
                })];
            var subOptions = tslib_1.__assign({}, opt);
            subOptions.depth = 0;
            _this.getSubEntities(entity, opt.depth).forEach(function (sub) {
                refPromises.push(_this.delete(sub, subOptions));
            });
            return Promise.all(refPromises).then(function () { return entity; });
        });
    };
    /**
     * Synchronize the persistence context to the underlying database.
     *
     * @return
     */
    EntityManager.prototype.flush = function () {
        throw new Error('Not implemented.');
    };
    /**
     * Make an instance managed and persistent.
     * @param entity - entity instance
     * @return
     */
    EntityManager.prototype.persist = function (entity) {
        this.attach(entity);
    };
    /**
     * Refresh the state of the instance from the database, overwriting changes made to the entity, if any.
     * @param entity - entity instance
     * @param options The refresh options
     * @return
     */
    EntityManager.prototype.refresh = function (entity, options) {
        if (!entity.id) {
            // entity is not persisted so far
            return Promise.resolve(entity);
        }
        return this.load(entity.id, undefined, tslib_1.__assign(tslib_1.__assign({}, options), { refresh: true }));
    };
    /**
     * Attach the instance to this database context, if it is not already attached
     * @param entity The entity to attach
     * @return
     */
    EntityManager.prototype.attach = function (entity) {
        if (!this.contains(entity)) {
            var type = this.metamodel.entity(entity.constructor);
            if (!type) {
                throw new error_1.IllegalEntityError(entity);
            }
            if (this.containsById(entity)) {
                throw new error_1.EntityExistsError(entity);
            }
            this._attach(entity);
        }
    };
    EntityManager.prototype._attach = function (entity) {
        var metadata = intersection_1.Metadata.get(entity);
        if (metadata.isAttached) {
            if (metadata.db !== this) {
                throw new error_1.EntityExistsError(entity);
            }
        }
        else {
            metadata.db = this;
        }
        if (!metadata.id) {
            if (metadata.type.name !== 'User' && metadata.type.name !== 'Role' && metadata.type.name !== 'logs.AppLog') {
                metadata.id = "".concat(DB_PREFIX + metadata.type.name, "/").concat((0, util_1.uuid)());
            }
        }
        if (metadata.id) {
            this.entities[metadata.id] = entity;
        }
    };
    EntityManager.prototype.removeReference = function (entity) {
        var state = intersection_1.Metadata.get(entity);
        if (!state || !state.id) {
            throw new error_1.IllegalEntityError(entity);
        }
        delete this.entities[state.id];
    };
    EntityManager.prototype.register = function (user, password, loginOption) {
        var _this = this;
        var login = loginOption > binding_1.LoginOption.NO_LOGIN;
        if (this.me && login) {
            throw new error_1.PersistentError('User is already logged in.');
        }
        return this.withLock(function () {
            var msg = new messages.Register({ user: user, password: password, login: login });
            return _this._userRequest(msg, loginOption);
        });
    };
    EntityManager.prototype.login = function (username, password, loginOption) {
        var _this = this;
        if (this.me) {
            throw new error_1.PersistentError('User is already logged in.');
        }
        return this.withLock(function () {
            var msg = new messages.Login({ username: username, password: password });
            return _this._userRequest(msg, loginOption);
        });
    };
    EntityManager.prototype.logout = function () {
        var _this = this;
        return this.withLock(function () { return _this.send(new messages.Logout()).then(_this._logout.bind(_this)); });
    };
    /**
     * Starts the MFA initiate process - note you must be logged in, to start the mfa setup process
     *
     * @returns A promise that resolves to an object with the following properties:
     * - qrCode: A Base64 representation of the QR code for MFA setup.
     * - keyUri: The URI for the MFA secret key.
     * @example
     * const { qrCode, keyUri } = await db.initMFA();
     * const code = await setupMFADevice(qrCode, keyUri);
     * const user = await db.finishMFA(code);
     */
    EntityManager.prototype.initMFA = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                return [2 /*return*/, this.send(new messages.MFAInitChallenge()).then(function (resp) {
                        return {
                            qrCode: resp.entity.qrCode,
                            keyUri: resp.entity.keyUri
                        };
                    })];
            });
        });
    };
    /**
     * Finishes the MFA (Multi-Factor Authentication) initiation process.
     *
     * @param code - The verification code for MFA.
     * @returns A promise that resolves with the user object of the logged-in user.
     */
    EntityManager.prototype.finishMFA = function (code) {
        var _this = this;
        return this.send(new messages.MFAInitFinish({ code: code })).then(function (resp) {
            return _this.User.me; // to be here user is already logged in;
        });
    };
    /**
     * Submit a verification code after a login
     *
     * @param code - A 6 digit verification code
     * @param token - An MFA token obtained during the login process
     * @return The logged-in user object
     */
    EntityManager.prototype.submitMFACode = function (code, token) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var loginType, msg;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                loginType = this.tokenStorage.temporary ? binding_1.LoginOption.SESSION_LOGIN : binding_1.LoginOption.PERSIST_LOGIN;
                msg = new messages.MFAToken({
                    authToken: token,
                    code: code,
                    global: loginType === binding_1.LoginOption.PERSIST_LOGIN,
                });
                return [2 /*return*/, this.withLock(function () { return _this._userRequest(msg, loginType); })];
            });
        });
    };
    /**
     * Disables Multi-Factor Authentication for the currently logged in user.
     *
     * @throws {PersistentError} - Thrown when the user is not logged in.
     * @return A promise that resolves when Multi-Factor Authentication is successfully disabled.
     */
    EntityManager.prototype.disableMFA = function () {
        if (!this.User.me)
            throw new error_1.PersistentError('User not Logged in');
        return this.send(new messages.MFADelete());
    };
    /**
     * Returns the current MFA status of the user
     *
     * @returns A promise that resolves to the MFA status of the user.
     * Possible values are 'ENABLED' if MFA is enabled, 'DISABLED' if MFA is
     * disabled, or 'PENDING' if MFA status is pending.
     */
    EntityManager.prototype.getMFAStatus = function () {
        return this.send(new messages.MFAStatus()).then(function (resp) { return resp.entity; });
    };
    EntityManager.prototype.loginWithOAuth = function (provider, options) {
        if (!this.connection) {
            throw new Error('This EntityManager is not connected.');
        }
        if (this.me) {
            throw new error_1.PersistentError('User is already logged in.');
        }
        var opt = tslib_1.__assign({ title: "Login with ".concat(provider), timeout: 5 * 60 * 1000, state: {}, loginOption: true, oAuthVersion: 2, open: util_1.openWindow }, options);
        if (opt.deviceCode) {
            return this._loginOAuthDevice(provider.toLowerCase(), opt);
        }
        if (opt.oAuthVersion !== 1 && !opt.path && !opt.deviceCode) {
            throw new Error('No OAuth path is provided to start the OAuth flow.');
        }
        if (opt.redirect) {
            Object.assign(opt.state, { redirect: opt.redirect, loginOption: opt.loginOption });
        }
        var oAuthEndpoint = "".concat(this.connection.origin).concat(this.connection.basePath, "/db/User/OAuth/").concat(provider.toLowerCase());
        var url = opt.oAuthVersion === 1 ? oAuthEndpoint : (0, Message_1.appendQueryParams)(opt.path, {
            client_id: opt.clientId,
            scope: opt.scope,
            state: JSON.stringify(opt.state),
            redirect_uri: oAuthEndpoint,
        });
        var windowOptions = {
            title: opt.title,
            width: opt.width,
            height: opt.height,
        };
        if (opt.redirect) {
            // use oauth via redirect by opening the login in the same window
            return opt.open(url, tslib_1.__assign({ target: '_self' }, windowOptions)) || url;
        }
        var req = this._userRequest(new connector_1.OAuthMessage(), opt.loginOption);
        if (!opt.open(url, windowOptions)) {
            throw new Error('The OAuth flow with a Pop-Up can only be issued in browsers. Add a redirect URL to the options to return to your app via that redirect after the OAuth flow succeed.');
        }
        return new Promise(function (resolve, reject) {
            var timeout = setTimeout(function () {
                reject(new error_1.PersistentError('OAuth login timeout.'));
            }, opt.timeout);
            req.then(resolve, reject).then(function () {
                clearTimeout(timeout);
            });
        });
    };
    EntityManager.prototype._loginOAuthDevice = function (provider, opt) {
        var _this = this;
        return this._userRequest(new messages.OAuth2(provider, opt.deviceCode), opt.loginOption)
            .catch(function () { return new Promise(function (resolve) { return setTimeout(resolve, 5000); })
            .then(function () { return _this._loginOAuthDevice(provider, opt); }); });
    };
    EntityManager.prototype.renew = function (loginOption) {
        var _this = this;
        return this.withLock(function () {
            var msg = new messages.Me();
            return _this._userRequest(msg, loginOption);
        });
    };
    EntityManager.prototype.newPassword = function (username, password, newPassword) {
        var _this = this;
        return this.withLock(function () {
            var msg = new messages.NewPassword({ username: username, password: password, newPassword: newPassword });
            return _this.send(msg, true).then(function (response) { return _this._updateUser(response.entity); });
        });
    };
    EntityManager.prototype.newPasswordWithToken = function (token, newPassword, loginOption) {
        var _this = this;
        return this.withLock(function () { return (_this._userRequest(new messages.NewPassword({ token: token, newPassword: newPassword }), loginOption)); });
    };
    EntityManager.prototype.resetPassword = function (username) {
        return this.send(new messages.ResetPassword({ username: username }));
    };
    EntityManager.prototype.changeUsername = function (username, newUsername, password) {
        return this.send(new messages.ChangeUsername({ username: username, newUsername: newUsername, password: password }));
    };
    EntityManager.prototype._updateUser = function (obj, updateMe) {
        if (updateMe === void 0) { updateMe = false; }
        var user = this.getReference(obj.id);
        var metadata = intersection_1.Metadata.get(user);
        metadata.type.fromJsonValue(metadata, obj, user, { persisting: true });
        if (updateMe) {
            this.me = user;
            if (this.connectData) {
                this.connectData.user = obj;
            }
        }
        return user;
    };
    EntityManager.prototype._logout = function () {
        this.me = null;
        this.token = null;
        if (this.connectData) {
            delete this.connectData.user;
        }
    };
    EntityManager.prototype._userRequest = function (msg, loginOption) {
        var _this = this;
        var opt = loginOption === undefined ? true : loginOption;
        var login = opt > binding_1.LoginOption.NO_LOGIN;
        if (login) {
            this.tokenStorage.temporary = opt < binding_1.LoginOption.PERSIST_LOGIN;
        }
        return this.send(msg, !login)
            .then(function (response) {
            return response.entity ? _this._updateUser(response.entity, login) : null;
        }, function (e) {
            if (e.status === connector_1.StatusCode.OBJECT_NOT_FOUND) {
                if (login) {
                    _this._logout();
                }
                return null;
            }
            if (e.status === connector_1.StatusCode.FORBIDDEN) {
                var data = e.data;
                throw new MFAError_1.MFAError(data['baqend-mfa-auth-token']); // If MFA is required: throw an error containing the auth token
            }
            throw e;
        });
    };
    /**
     * @param deviceType The OS of the device (IOS/Android)
     * @param subscription WebPush subscription
     * @param device
     * @return
     */
    EntityManager.prototype.registerDevice = function (deviceType, subscription, device) {
        var _this = this;
        var msg = new messages.DeviceRegister({ devicetype: deviceType, subscription: subscription, device: device });
        msg.withCredentials = true;
        return this.send(msg)
            .then(function (response) { return _this._updateDevice(response.entity); });
    };
    EntityManager.prototype._updateDevice = function (obj) {
        var device = this.getReference(obj.id);
        var metadata = intersection_1.Metadata.get(device);
        metadata.type.fromJsonValue(metadata, obj, device, { persisting: true });
        this.deviceMe = device;
        if (this.connectData) {
            this.connectData.device = obj;
        }
        return device;
    };
    EntityManager.prototype.checkDeviceRegistration = function () {
        return this.send(new messages.DeviceRegistered())
            .then(function () { return true; }, function (e) {
            if (e.status === connector_1.StatusCode.OBJECT_NOT_FOUND) {
                return false;
            }
            throw e;
        });
    };
    EntityManager.prototype.pushDevice = function (pushMessage) {
        return this.send(new messages.DevicePush(pushMessage.toJSON()));
    };
    /**
     * The given entity will be checked by the validation code of the entity type.
     *
     * @param entity
     * @return result
     */
    EntityManager.prototype.validate = function (entity) {
        var type = intersection_1.Metadata.get(entity).type;
        var result = new intersection_1.ValidationResult();
        var iter = type.attributes();
        for (var item = iter.next(); !item.done; item = iter.next()) {
            var validate = new intersection_1.Validator(item.value.name, entity);
            result.fields[validate.key] = validate;
        }
        var validationCode = type.validationCode;
        if (validationCode) {
            validationCode(result.fields);
        }
        return result;
    };
    /**
     * Adds the given object id to the cacheWhiteList if needed.
     * @param objectId The id to add.
     * @return
     */
    EntityManager.prototype.addToWhiteList = function (objectId) {
        if (this.isCachingEnabled()) {
            if (this.bloomFilter.contains(objectId) || this.cacheBlackList.has(objectId)) {
                this.cacheWhiteList.add(objectId);
            }
        }
    };
    /**
     * Adds the given object id to the cacheBlackList if needed.
     * @param objectId The id to add.
     * @return
     */
    EntityManager.prototype.addToBlackList = function (objectId) {
        if (this.isCachingEnabled() && objectId) {
            if (!this.bloomFilter.contains(objectId)) {
                this.cacheBlackList.add(objectId);
            }
            this.cacheWhiteList.delete(objectId);
        }
    };
    EntityManager.prototype.refreshBloomFilter = function () {
        var _this = this;
        if (!this.isCachingEnabled()) {
            return Promise.resolve(null);
        }
        var msg = new messages.GetBloomFilter();
        msg.noCache();
        return this.send(msg).then(function (response) {
            _this._updateBloomFilter(response.entity);
            return _this.bloomFilter;
        });
    };
    EntityManager.prototype._updateBloomFilter = function (bloomFilter) {
        this.bloomFilter = new caching_1.BloomFilter(bloomFilter);
        this.cacheWhiteList = new Set();
        this.cacheBlackList = new Set();
    };
    /**
     * Checks the freshness of the bloom filter and does a reload if necessary
     */
    EntityManager.prototype.ensureBloomFilterFreshness = function () {
        var _this = this;
        if (!this.isCachingEnabled()) {
            return;
        }
        var now = new Date().getTime();
        var refreshRate = this.bloomFilterRefresh * 1000;
        if (this.bloomFilterLock.isReady && now - this.bloomFilter.creation > refreshRate) {
            this.bloomFilterLock.withLock(function () { return _this.refreshBloomFilter(); });
        }
    };
    /**
     * Checks for a given id, if revalidation is required, the resource is stale or caching was disabled
     * @param id The object id to check
     * @return Indicates if the resource must be revalidated
     */
    EntityManager.prototype.mustRevalidate = function (id) {
        if (util_1.isNode) {
            return false;
        }
        this.ensureBloomFilterFreshness();
        if (!this.isCachingEnabled() || !this.bloomFilterLock.isReady) {
            return true;
        }
        if (Message_1.CACHE_REPLACEMENT_SUPPORTED && this.cacheWhiteList.has(id)) {
            return false;
        }
        return this.cacheBlackList.has(id) || this.bloomFilter.contains(id);
    };
    /**
     * @param id To check the bloom filter
     * @param message To attach the headers
     * @param refresh To force the reload headers
     * @return
     */
    EntityManager.prototype.ensureCacheHeader = function (id, message, refresh) {
        var noCache = refresh || !id || this.mustRevalidate(id);
        if (noCache) {
            message.noCache();
        }
    };
    /**
     * Creates a absolute url for the given relative one
     * @param relativePath the relative url
     * @param authorize indicates if authorization credentials should be generated and be attached to the url
     * @return a absolute url which is optionally signed with a resource token which authenticates the currently
     * logged in user
     */
    EntityManager.prototype.createURL = function (relativePath, authorize) {
        var _this = this;
        var connection = this.connection;
        if (!connection) {
            throw new Error('This EntityManager is not connected.');
        }
        return this.tokenStorage.signPath(connection.basePath + relativePath, authorize)
            .then(function (path) {
            var url = connection.origin + path;
            if (_this.mustRevalidate(relativePath)) {
                url += "".concat(authorize ? '&' : '?', "BCB");
            }
            return url;
        });
    };
    /**
     * Requests a perpetual token for the given user
     *
     * Only users with the admin role are allowed to request an API token.
     *
     * @param entityClass
     * @param user The user object or id of the user object
     * @return
     */
    EntityManager.prototype.requestAPIToken = function (entityClass, user) {
        var userObj = this._getUserReference(entityClass, user);
        var msg = new messages.UserToken(userObj.key);
        return this.send(msg).then(function (resp) { return resp.entity; });
    };
    /**
     * Revoke all created tokens for the given user
     *
     * This method will revoke all previously issued tokens and the user must login again.
     *
     * @param entityClass
     * @param user The user object or id of the user object
     */
    EntityManager.prototype.revokeAllTokens = function (entityClass, user) {
        var userObj = this._getUserReference(entityClass, user);
        var msg = new messages.RevokeUserToken(userObj.key);
        return this.send(msg);
    };
    EntityManager.prototype.executeQuery = function (resultClass, query, triggeredBy, ttl) {
        var _a;
        var type = resultClass ? this.metamodel.entity(resultClass) : null;
        var backendType = type === null || type === void 0 ? void 0 : type.getMetadata("backendType");
        if (!type || !backendType || backendType !== "analytics") {
            throw new Error("Only analytics entities can execute queries");
        }
        var uriSize = (((_a = this.connection) === null || _a === void 0 ? void 0 : _a.host.length) || 0) + query.length;
        var msg;
        if (uriSize > query_1.Query.MAX_URI_SIZE) {
            msg = new messages.AdhocQueryPOST(type.name, undefined, undefined, undefined, triggeredBy, ttl, query)
                .entity(query, 'text');
        }
        else {
            msg = new message.AdhocQuery(type.name, query, undefined, undefined, undefined, undefined, undefined, triggeredBy, ttl);
        }
        return this.send(msg).then(function (response) { return response.entity; });
    };
    EntityManager.prototype._getUserReference = function (entityClass, user) {
        if (typeof user === 'string') {
            return this.getReference(entityClass, user);
        }
        return user;
    };
    tslib_1.__decorate([
        (0, util_1.deprecated)('EntityManager.isCachingEnabled()')
    ], EntityManager.prototype, "isCachingDisabled", null);
    return EntityManager;
}(util_1.Lockable));
exports.EntityManager = EntityManager;
//# sourceMappingURL=data:application/json;base64,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